import type { ReactNode } from "react";
import { useIntl } from "react-intl";

import type { ScheduledPostPreview } from "@/lib/scheduled-post-preview";

import {
  getSocialPostPreviewCardCaptionText,
  getSocialPostPreviewCardFormatDescriptor,
  InstagramPostCard,
  InstagramPostVisualFallback,
} from "./instagram-post-card";
import { SocialPostPreview } from "./social-post-preview";

export {
  getImportedInstagramPostFormatDescriptor,
  getInstagramPostMetricValue,
  getSocialPostPreviewCardCaptionText,
  getSocialPostPreviewCardFormatDescriptor,
  getSocialPostPreviewCardHashtagSummary,
  ImportedInstagramPostCard,
  resolveImportedInstagramPostImage,
  type ImportedInstagramPostCardMedia,
} from "./instagram-post-card";

export type SocialPostPreviewCardProps = {
  preview: ScheduledPostPreview;
  timeLabel: string;
  profileLabel: string;
  approvalLabel?: string;
  showApprovalBadge?: boolean;
  muted?: boolean;
  onOpen?: () => void;
  onEdit?: () => void;
  mediaOverlay?: ReactNode;
  emptyVisual?: ReactNode;
  className?: string;
  captionFallback?: string;
  emptyVisualTitle?: string;
  emptyVisualDescription?: string;
};

export function SocialPostPreviewCard({
  preview,
  timeLabel,
  profileLabel,
  approvalLabel,
  showApprovalBadge = false,
  muted = false,
  onOpen,
  onEdit,
  mediaOverlay,
  emptyVisual,
  className,
  captionFallback,
  emptyVisualTitle,
  emptyVisualDescription,
}: SocialPostPreviewCardProps) {
  const intl = useIntl();
  const caption = getSocialPostPreviewCardCaptionText(
    preview.caption,
    captionFallback ??
      intl.formatMessage({
        id: "socialPostCard.captionPending",
        defaultMessage: "Caption pending",
      }),
  );
  const formatLabel = intl.formatMessage(
    getSocialPostPreviewCardFormatDescriptor(preview.plan?.postType),
  );

  const visual = preview.plan ? (
    <SocialPostPreview
      renderMode="preview"
      platform={preview.plan.platform}
      type={preview.plan.postType}
      plan={preview.plan}
      linkedAssets={preview.linkedAssets}
      brandkit={preview.brandkit}
      showMetadata={false}
    />
  ) : preview.previewAsset ? (
    <img
      src={preview.previewAsset.signedUrl}
      alt={caption}
      width={preview.previewAsset.width ?? 1080}
      height={preview.previewAsset.height ?? 1350}
      className="block w-full object-cover"
      style={{
        aspectRatio: `${preview.previewAsset.width ?? 1080} / ${preview.previewAsset.height ?? 1350}`,
      }}
      loading="lazy"
    />
  ) : emptyVisual ? (
    emptyVisual
  ) : (
    <InstagramPostVisualFallback
      title={
        emptyVisualTitle ??
        intl.formatMessage({
          id: "socialPostCard.previewPendingTitle",
          defaultMessage: "Preview pending",
        })
      }
      description={emptyVisualDescription}
    />
  );

  return (
    <InstagramPostCard
      formatLabel={formatLabel}
      timeLabel={timeLabel}
      profileLabel={profileLabel}
      caption={caption}
      hashtags={preview.hashtags}
      visual={visual}
      approvalLabel={approvalLabel}
      showApprovalBadge={showApprovalBadge}
      muted={muted}
      onOpen={onOpen}
      onEdit={onEdit}
      mediaOverlay={mediaOverlay}
      className={className}
    />
  );
}
